// 4-step profile builder. Photos + Music are placeholder steps in M2.1
// (real impls land in M2.2 + M2.6); Basics + Bio are wired against the
// backend. Each field change debounces 500ms then PATCH /profiles/me.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Gender,
  LookingFor,
  Profile,
  ProfilePatch,
  profilesApi,
  sessionStore,
  APIError,
  MediaItem,
  mediaApi,
  uploadPhoto,
  ALLOWED_PHOTO_TYPES,
  spotifyApi,
  SpotifyConnection,
  api,
} from '@/lib/api';
import { MusicSplit } from '@/components/MusicCards';
import { Icons, SignOutConfirm, useSignOutConfirm } from '@/components';
import { friendlyError } from '@/lib/errors';
import './Builder.css';

const STEPS = ['Photos', 'Basics', 'Age', 'Bio', 'Music'] as const;
type StepIdx = 0 | 1 | 2 | 3 | 4;
const MUSIC_STEP: StepIdx = 4;

// sessionStorage key used to carry "land on step N" across the
// Spotify OAuth round-trip. Set before window.location.href to
// Spotify; read + cleared on builder mount. Survives because
// sessionStorage is per-tab and persists across same-origin
// navigations. More reliable than a ?step= URL param, which loses
// the race against the MusicStep's URL-cleanup effect (especially in
// React StrictMode dev where the lazy initializer can run twice).
const BUILDER_STEP_HANDOFF_KEY = 'wydm.builder.intended_step';

export function BuilderPage() {
  const navigate = useNavigate();
  const signOutConfirm = useSignOutConfirm();
  const signOutTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [step, setStep] = useState<StepIdx>(() => {
    // Two ways to land on a non-zero step:
    //   1. sessionStorage handoff (set by MusicStep before Spotify OAuth)
    //   2. ?spotify=... in the URL (fallback if sessionStorage was wiped)
    const stored = sessionStorage.getItem(BUILDER_STEP_HANDOFF_KEY);
    if (stored) {
      sessionStorage.removeItem(BUILDER_STEP_HANDOFF_KEY);
      const n = parseInt(stored, 10);
      if (n >= 0 && n <= MUSIC_STEP) return n as StepIdx;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.has('spotify')) return MUSIC_STEP;
    return 0;
  });
  const [profile, setProfile] = useState<Profile | null>(null);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);

  // Generation counter: every mutation (upload/delete/reorder) and every
  // direct setMedia from the user bumps this. The scan-polling fetch tags
  // itself with the generation that was current when it started; if media
  // changed while the fetch was in flight, its response is discarded. Fixes
  // a race where a deleted photo would "reappear" because a poller's
  // already-in-flight GET (with the row still present) landed after the
  // DELETE completed and overwrote local state.
  const mediaGenRef = useRef(0);
  const setMediaFresh = useCallback((next: MediaItem[] | ((prev: MediaItem[]) => MediaItem[])) => {
    mediaGenRef.current += 1;
    setMedia(next as MediaItem[]);
  }, []);

  // Load profile + media on mount.
  useEffect(() => {
    if (!sessionStore.getAccess()) {
      navigate('/auth/signin');
      return;
    }
    profilesApi.me().then(setProfile).catch((err) => {
      setLoadError(friendlyError(err, 'Builder'));
    });
    profilesApi.listMyMedia().then(setMediaFresh).catch(() => null);
  }, [navigate, setMediaFresh]);

  // While any photo is mid-scan, refetch every 2s so the UI flips to
  // published / rejected as the worker finishes. Stops as soon as no
  // scanning_nsfw rows remain.
  useEffect(() => {
    const anyScanning = media.some((m) => m.state === 'scanning_nsfw');
    if (!anyScanning) return;
    const t = setInterval(() => {
      const genAtStart = mediaGenRef.current;
      profilesApi.listMyMedia()
        .then((next) => {
          // Discard stale response — a mutation happened while we were
          // in flight, so the local state is more authoritative.
          if (mediaGenRef.current !== genAtStart) return;
          setMedia(next);
        })
        .catch(() => null);
    }, 2000);
    return () => clearInterval(t);
  }, [media]);

  // Local form state mirrors the loaded profile + lets the user edit before
  // the debounced save kicks in. Initialized once profile loads.
  const [draft, setDraft] = useState<{
    bio: string;
    gender: Gender | '';
    lookingFor: LookingFor[];
  }>({ bio: '', gender: '', lookingFor: [] });

  useEffect(() => {
    if (!profile) return;
    setDraft({
      bio: profile.bio ?? '',
      gender: profile.gender ?? '',
      lookingFor: profile.looking_for ?? [],
    });
  }, [profile?.id]); // re-init only when the profile id changes (first load)

  // Debounced sync — every change to draft schedules a single PATCH 500ms later.
  const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSync = useCallback((patch: ProfilePatch) => {
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      profilesApi
        .patch(patch)
        .then((p) => setProfile(p))
        .catch(() => null); // swallow — local state is the source of truth between syncs
    }, 500);
  }, []);

  const updateDraft = useCallback(
    (next: Partial<typeof draft>) => {
      setDraft((d) => {
        const merged = { ...d, ...next };
        const patch: ProfilePatch = {};
        if ('bio' in next) patch.bio = merged.bio;
        if ('gender' in next) patch.gender = merged.gender;
        if ('lookingFor' in next) patch.looking_for = merged.lookingFor;
        scheduleSync(patch);
        return merged;
      });
    },
    [scheduleSync],
  );

  const user = sessionStore.getUser();

  // Mirror the backend's ValidatePublish so we explain *why* publish is
  // disabled rather than just greying out the button. Order matches the
  // backend's checks (most actionable reason first).
  const publishBlocker = useMemo<string | null>(() => {
    if (!user?.display_name || user.display_name.length < 2) {
      return 'Set your display name on the Basics step to continue.';
    }
    if (!user?.dob) {
      return 'Set your date of birth on the Age step to continue.';
    }
    if (draft.lookingFor.length === 0) {
      return "Pick at least one option in Looking for on the Basics step.";
    }
    const published = media.filter((m) => m.state === 'published').length;
    const scanning = media.filter((m) => m.state === 'scanning_nsfw').length;
    const rejected = media.filter(
      (m) => m.state === 'rejected_nsfw' || m.state === 'csam_match',
    ).length;
    if (scanning > 0) {
      return `Scanning ${scanning} photo${scanning === 1 ? '' : 's'} — usually a few seconds.`;
    }
    if (rejected > 0) {
      return rejected === 1
        ? 'One of your photos was rejected for nudity. Remove it before publishing.'
        : `${rejected} of your photos were rejected for nudity. Remove them before publishing.`;
    }
    if (published === 0) {
      return 'Add at least one photo on the Photos step.';
    }
    return null;
  }, [user, draft.lookingFor, media]);

  const canPublish = publishBlocker === null;

  const doPublish = async () => {
    setPublishError(null);
    setPublishing(true);
    try {
      // Flush any pending debounce first.
      if (syncTimer.current) {
        clearTimeout(syncTimer.current);
        await profilesApi.patch({
          bio: draft.bio,
          gender: draft.gender,
          looking_for: draft.lookingFor,
        });
      }
      const resp = await profilesApi.publish();
      setProfile(resp.profile);
      navigate('/home');
    } catch (err) {
      setPublishError(friendlyError(err, 'Builder'));
    } finally {
      setPublishing(false);
    }
  };

  if (loadError) {
    return (
      <main style={{ maxWidth: 480, margin: '0 auto', padding: '80px 24px' }}>
        <ErrorBox>{loadError}</ErrorBox>
      </main>
    );
  }
  if (!profile) {
    return (
      <main style={{ maxWidth: 480, margin: '0 auto', padding: '120px 24px', textAlign: 'center' }}>
        <p style={{ color: 'var(--ink-3)' }}>Loading…</p>
      </main>
    );
  }

  const isLast = step === MUSIC_STEP;

  return (
    <main className="builder-shell">
      {/* Header: paired nav arrows on the left, step label centered,
          save-and-exit on the right. ›  hidden on mobile via CSS so the
          bottom Continue button stays the primary forward nav there. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            type="button"
            onClick={() => (step > 0 ? setStep((s) => (s - 1) as StepIdx) : navigate('/home'))}
            style={iconBtn}
            aria-label="Back"
          >
            ‹
          </button>
          {!isLast && (
            <button
              type="button"
              onClick={() => setStep((s) => (s + 1) as StepIdx)}
              className="builder-header-next"
              style={iconBtn}
              aria-label="Next step"
            >
              ›
            </button>
          )}
        </div>
        {/* Step counter ("01 / 05 · PHOTOS") — hidden on mobile.
            The progress bar two lines down already shows the position;
            the counter was duplicating the same signal + crowding the
            header on narrow widths. Desktop keeps it as a centred
            scanability label between the back button + the right-edge
            actions. */}
        <div
          className="mono hide-on-mobile"
          style={{ fontSize: 12, color: 'var(--ink-3)', letterSpacing: 1 }}
        >
          {String(step + 1).padStart(2, '0')} / {String(STEPS.length).padStart(2, '0')} · {STEPS[step].toUpperCase()}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button type="button" onClick={() => navigate('/home')} style={textBtn}>
            Save &amp; exit
          </button>
          <button
            type="button"
            ref={signOutTriggerRef}
            onClick={signOutConfirm.request}
            style={{ ...textBtn, display: 'inline-flex', alignItems: 'center', gap: 5 }}
            aria-label="Sign out"
            title="Sign out"
          >
            <Icons.signout size={13} />
            Sign out
          </button>
        </div>
      </div>
      <SignOutConfirm
        open={signOutConfirm.open}
        onCancel={signOutConfirm.cancel}
        triggerRef={signOutTriggerRef}
      />

      {/* Progress bar */}
      <div style={{ display: 'flex', gap: 3, marginBottom: 14 }}>
        {STEPS.map((_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 3,
              background: i <= step ? 'var(--coral)' : 'var(--line-strong)',
              transition: 'background 0.3s',
            }}
          />
        ))}
      </div>

      {/* Global scan-in-progress banner — visible on every step so the
          user knows publish will unlock once scans settle. */}
      <ScanBanner media={media} />

      {/* Step body */}
      <div key={step} style={{ animation: 'rise 0.35s ease' }}>
        {step === 0 && <PhotosStep media={media} onChange={setMediaFresh} />}
        {step === 1 && (
          <BasicsStep
            displayName={user?.display_name ?? ''}
            gender={draft.gender}
            lookingFor={draft.lookingFor}
            onChange={updateDraft}
          />
        )}
        {step === 2 && <AgeStep />}
        {step === 3 && <BioStep bio={draft.bio} onChange={(bio) => updateDraft({ bio })} />}
        {step === 4 && <MusicStep />}
      </div>

      {publishError && <ErrorBox>{publishError}</ErrorBox>}

      {/* Footer CTA — flat (no sticky platform). On desktop, the
          Continue is hidden via .builder-footer-continue (the header
          › arrow replaces it); Publish on the last step stays visible
          on both viewports. */}
      <div className="builder-footer">
        {isLast ? (
          <>
            <button
              type="button"
              onClick={doPublish}
              disabled={!canPublish || publishing}
              className="builder-footer-cta"
              style={btnPrimary(canPublish && !publishing)}
            >
              {publishing ? 'Publishing…' : profile.published ? 'Update profile' : 'Publish profile'}
            </button>
            {publishBlocker && (
              <p
                className="builder-footer-hint"
                style={{
                  marginTop: 10,
                  fontSize: 12.5,
                  color: 'var(--ink-3)',
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                {publishBlocker}
              </p>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => setStep((s) => (s + 1) as StepIdx)}
            className="builder-footer-cta builder-footer-continue"
            style={btnPrimary(true)}
          >
            Continue →
          </button>
        )}
      </div>
    </main>
  );
}

// ── steps ──────────────────────────────────────────────────────────────────

function ScanBanner({ media }: { media: MediaItem[] }) {
  const scanning = media.filter((m) => m.state === 'scanning_nsfw').length;
  const rejected = media.filter(
    (m) => m.state === 'rejected_nsfw' || m.state === 'csam_match',
  ).length;
  if (scanning === 0 && rejected === 0) {
    return <div style={{ marginBottom: 14 }} />;
  }
  return (
    <div style={{ marginBottom: 14 }}>
      {scanning > 0 && (
        <div style={scanBanner}>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 10, background: 'var(--coral)', marginRight: 8, verticalAlign: 'middle' }} />
          Scanning {scanning} photo{scanning === 1 ? '' : 's'}… publish will unlock when this finishes.
        </div>
      )}
      {rejected > 0 && (
        <div style={rejectedBanner}>
          {rejected === 1
            ? '1 photo was rejected for nudity. Remove it on the Photos step before publishing.'
            : `${rejected} photos were rejected for nudity. Remove them on the Photos step before publishing.`}
        </div>
      )}
    </div>
  );
}

// Hard ceiling — must match maxPhotosPerProfile in backend/internal/media/handlers.go
// and the CHECK constraint in migrations/0010.
const MAX_PHOTOS = 30;
// Default visible slot count before the grid starts growing. Two rows of 3.
const DEFAULT_SLOTS = 6;

function PhotosStep({
  media,
  onChange,
}: {
  media: MediaItem[];
  onChange: (next: MediaItem[]) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Sorted by position. Positions are kept contiguous 1..N by the recompact
  // call after delete + the server allocating the lowest free slot on upload.
  const sorted = useMemo(
    () => [...media].sort((a, b) => a.position - b.position),
    [media],
  );

  const readyCount = sorted.filter((m) => m.state === 'published').length;

  // Slot count grows by 1 whenever the trailing slot fills. Floor at 6 (the
  // default empty state shows 2 rows of 3); ceiling at MAX_PHOTOS.
  //   0 photos → 6 slots,  6 photos → 7 slots,  7 photos → 8 slots, …
  //   30 photos → 30 slots (cap; no trailing empty pad)
  const slotCount = Math.min(
    MAX_PHOTOS,
    Math.max(DEFAULT_SLOTS, sorted.length + 1),
  );
  const emptyPadCount = Math.max(0, slotCount - sorted.length);

  const handleUpload = async (file: File) => {
    setErr(null);
    setUploading(true);
    try {
      // No position hint — server allocates the lowest free slot, which is
      // sorted.length + 1 given contiguous positions.
      const item = await uploadPhoto(file);
      onChange([...media, item].sort((a, b) => a.position - b.position));
    } catch (e) {
      setErr(friendlyError(e, 'Builder'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item: MediaItem) => {
    setErr(null);
    try {
      await mediaApi.delete(item.id);
      const remaining = sorted.filter((m) => m.id !== item.id);
      if (remaining.length === 0) {
        onChange([]);
        return;
      }
      // Recompact: shift trailing photos back so positions stay 1..N. This
      // matters because the upload allocator picks the lowest free slot —
      // a hole in the middle would mean the next upload lands there
      // instead of at the end, which isn't what the user expects after a
      // delete-then-add.
      try {
        const reordered = await profilesApi.reorderMedia(remaining.map((m) => m.id));
        onChange(reordered);
      } catch {
        // Recompact failed — surface the remaining photos at their old
        // (now-sparse) positions. The next reorder or delete will retry.
        onChange(remaining);
      }
    } catch (e) {
      setErr(friendlyError(e, 'Builder'));
    }
  };

  // ── dnd-kit ─────────────────────────────────────────────────────────────
  // PointerSensor with a small activation distance so a tap-to-click on the
  // × remove button doesn't accidentally fire a drag. Keyboard sensor lets
  // Tab + Space + arrows reorder for a11y.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));

  const onDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const fromIdx = sorted.findIndex((m) => m.id === active.id);
    const toIdx = sorted.findIndex((m) => m.id === over.id);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = arrayMove(sorted, fromIdx, toIdx);
    // Optimistic — assign fresh positions so the grid re-renders in the
    // new order while the PUT is in flight.
    const optimistic = next.map((m, i) => ({ ...m, position: i + 1 }));
    onChange(optimistic);
    try {
      const server = await profilesApi.reorderMedia(next.map((m) => m.id));
      onChange(server);
    } catch (e) {
      onChange(sorted);
      setErr(friendlyError(e, 'Builder'));
    }
  };

  const pickFile = () => fileInputRef.current?.click();

  const activeItem = activeId ? sorted.find((m) => m.id === activeId) ?? null : null;
  const activeIdx = activeItem ? sorted.findIndex((m) => m.id === activeItem.id) : -1;

  return (
    <StepShell
      eyebrow="01 · Photos"
      title="Show us who you are."
      sub={[
        `Up to ${MAX_PHOTOS}. Your first photo is the lead — make it clear, face-forward, recent. Drag to reorder.`,
        "Clear face in photo 1, include a full-body shot, show one hobby. Skip group shots in the lead — raters can't find you.",
      ]}
    >
      {/* Shared hidden input — every empty-slot button triggers this. */}
      <input
        type="file"
        ref={fileInputRef}
        accept={ALLOWED_PHOTO_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleUpload(f);
          e.target.value = ''; // allow re-upload of the same file
        }}
      />

      <div
        className="mono"
        style={{
          fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase',
          marginBottom: 10, display: 'flex', justifyContent: 'space-between',
        }}
      >
        <span>JPEG / PNG / WebP · ≤ 10MB</span>
        <span>{readyCount}/{sorted.length || DEFAULT_SLOTS} ready</span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div className="builder-photo-grid">
          <SortableContext items={sorted.map((m) => m.id)} strategy={rectSortingStrategy}>
            {sorted.map((item, idx) => (
              <SortablePhoto
                key={item.id}
                item={item}
                isLead={idx === 0 && item.state === 'published'}
                onDelete={() => handleDelete(item)}
              />
            ))}
          </SortableContext>
          {Array.from({ length: emptyPadCount }).map((_, i) => {
            const slotIdx = sorted.length + i;
            const isTrailingUploading = i === 0 && uploading;
            return (
              <div key={`empty-${slotIdx}`} style={photoSlotBase(false)}>
                {isTrailingUploading ? (
                  <div style={uploadingOverlay}>
                    <div className="mono" style={{ fontSize: 10, color: '#fff', letterSpacing: 0.5 }}>UPLOADING</div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={pickFile}
                    disabled={uploading}
                    style={{ ...emptySlotBtn, opacity: uploading ? 0.5 : 1 }}
                  >
                    <span style={{ fontSize: 22, color: 'var(--ink-4)' }}>+</span>
                    <span
                      className="mono"
                      style={{ fontSize: 9, color: 'var(--ink-4)', letterSpacing: 1 }}
                    >
                      {String(slotIdx + 1).padStart(2, '0')}
                    </span>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* DragOverlay floats a copy of the dragged photo under the cursor —
            without it, the original would just translate via CSS and you'd
            lose the "lifted" feeling. Disable React rendering inside via the
            isOverlay flag so the lead badge etc. still show. */}
        <DragOverlay dropAnimation={{ duration: 220, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
          {activeItem ? (
            <PhotoCard
              item={activeItem}
              isLead={activeIdx === 0 && activeItem.state === 'published'}
              isOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {sorted.length >= MAX_PHOTOS && (
        <p
          className="mono"
          style={{
            fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1, textAlign: 'center',
            marginTop: 12, textTransform: 'uppercase',
          }}
        >
          Cap reached · {MAX_PHOTOS} photos
        </p>
      )}

      {err && (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', borderRadius: 12, padding: '10px 14px', fontSize: 13, marginTop: 14 }}>
          {err}
        </div>
      )}

      {/* (Tip card removed — those tips now live as the second sub
          paragraph at the top of the step.) */}
    </StepShell>
  );
}

// SortablePhoto — a single grid cell wired into dnd-kit. The transform +
// transition props from useSortable drive the FLIP-style animation while
// other items shift to make room.
function SortablePhoto({
  item,
  isLead,
  onDelete,
}: {
  item: MediaItem;
  isLead: boolean;
  onDelete: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    ...photoSlotBase(true),
    transform: CSS.Transform.toString(transform),
    transition: transition ?? 'transform 220ms cubic-bezier(0.2, 0, 0, 1)',
    // Source slot fades out — the DragOverlay shows the lifted copy.
    opacity: isDragging ? 0 : 1,
    cursor: 'grab',
    touchAction: 'none', // required for PointerSensor on touch devices
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <PhotoCard item={item} isLead={isLead} onDelete={onDelete} />
    </div>
  );
}

// PhotoCard — the visual content of a filled slot. Pulled out so both
// SortablePhoto and DragOverlay render the same markup.
function PhotoCard({
  item,
  isLead,
  onDelete,
  isOverlay = false,
}: {
  item: MediaItem;
  isLead: boolean;
  onDelete?: () => void;
  isOverlay?: boolean;
}) {
  return (
    <div
      style={
        isOverlay
          ? {
              ...photoSlotBase(true),
              boxShadow: '0 18px 40px rgba(20, 20, 40, 0.25)',
              transform: 'scale(1.05)',
              cursor: 'grabbing',
            }
          : { width: '100%', height: '100%', position: 'relative' }
      }
    >
      <img
        src={item.url}
        alt=""
        draggable={false}
        style={{
          width: '100%', height: '100%', objectFit: 'cover',
          pointerEvents: 'none', borderRadius: 'inherit',
        }}
      />
      {isLead && <div style={leadBadge}>LEAD</div>}
      {item.state === 'scanning_nsfw' && (
        <div style={uploadingOverlay}>
          <div className="mono" style={{ fontSize: 10, color: '#fff', letterSpacing: 0.5 }}>SCANNING</div>
        </div>
      )}
      {item.state === 'rejected_nsfw' && (
        <div style={rejectedOverlay}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: 0.5, marginBottom: 4 }}>REJECTED</div>
          <div style={{ fontSize: 11, opacity: 0.9, textAlign: 'center', padding: '0 6px' }}>
            for nudity
          </div>
        </div>
      )}
      {onDelete && !isOverlay && (
        <button
          type="button"
          // Stop the click from reaching the dnd listeners on the parent.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          style={removeBtn}
          aria-label="Remove photo"
          title="Remove"
        >×</button>
      )}
    </div>
  );
}

function BasicsStep({
  displayName,
  gender,
  lookingFor,
  onChange,
}: {
  displayName: string;
  gender: Gender | '';
  lookingFor: LookingFor[];
  onChange: (next: { gender?: Gender | ''; lookingFor?: LookingFor[] }) => void;
}) {
  const toggleLF = (v: LookingFor) => {
    onChange({
      lookingFor: lookingFor.includes(v)
        ? lookingFor.filter((x) => x !== v)
        : [...lookingFor, v],
    });
  };

  // Editable display name. Debounced save via the existing
  // /auth/profile/complete endpoint (relaxed to accept partial
  // updates so we can send display_name without dob). On success,
  // the cached user in sessionStore is refreshed so other screens
  // (header chrome, public profile re-fetch) see the new name.
  const [name, setName] = useState(displayName);
  const [nameStatus, setNameStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [nameErr, setNameErr] = useState<string | null>(null);
  const nameTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleNameChange = (value: string) => {
    setName(value);
    setNameErr(null);
    if (nameTimer.current) clearTimeout(nameTimer.current);
    const trimmed = value.trim();
    // Client-side length guard matches the backend (2..30). Below 2,
    // skip the save — the input is probably mid-edit.
    if (trimmed.length < 2 || trimmed.length > 30) return;
    if (trimmed === displayName) return; // no-op
    nameTimer.current = setTimeout(async () => {
      setNameStatus('saving');
      try {
        const updated = await api.completeProfile({ display_name: trimmed });
        const access = sessionStore.getAccess();
        const refresh = sessionStore.getRefresh();
        if (access && refresh) sessionStore.set(access, refresh, updated);
        setNameStatus('saved');
        setTimeout(() => setNameStatus((s) => (s === 'saved' ? 'idle' : s)), 1500);
      } catch (e) {
        setNameStatus('error');
        setNameErr(friendlyError(e, 'Builder'));
      }
    }, 600);
  };

  return (
    <StepShell eyebrow="02 · Basics">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Field
          label={
            <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
              <span>Display name</span>
              {nameStatus === 'saving' && (
                <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1 }}>Saving…</span>
              )}
              {nameStatus === 'saved' && (
                <span className="mono" style={{ fontSize: 11, color: 'var(--coral)', letterSpacing: 1 }}>Saved</span>
              )}
            </span>
          }
        >
          <input
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="What do people call you?"
            maxLength={30}
            style={{
              ...readOnlyField,
              fontFamily: 'var(--font-ui)',
              fontSize: 16,
              color: 'var(--ink)',
              cursor: 'text',
              border: '1.5px solid var(--line)',
            }}
          />
          {nameErr && (
            <div style={{ marginTop: 8, background: 'rgba(208,64,48,0.08)', color: '#a8331f', borderRadius: 10, padding: '8px 12px', fontSize: 12.5 }}>
              {nameErr}
            </div>
          )}
        </Field>
        <Field label="I am">
          <ChipRow>
            {([['woman', 'Woman'], ['man', 'Man']] as Array<[Gender, string]>).map(([k, l]) => (
              <Chip key={k} active={gender === k} onClick={() => onChange({ gender: gender === k ? '' : k })}>
                {l}
              </Chip>
            ))}
          </ChipRow>
        </Field>
        <Field label={`Looking for · ${lookingFor.length} selected`}>
          <ChipRow>
            {(
              [
                ['men', 'Men'],
                ['women', 'Women'],
                ['everyone', 'Everyone'],
              ] as Array<[LookingFor, string]>
            ).map(([k, l]) => (
              <Chip key={k} active={lookingFor.includes(k)} onClick={() => toggleLF(k)}>
                {l}
              </Chip>
            ))}
          </ChipRow>
        </Field>
      </div>
    </StepShell>
  );
}

// AgeStep — change date of birth. The value lives on users.dob (set
// once during signup), so we read it from sessionStore on mount and
// persist updates via api.completeProfile (idempotent — the handler
// updates dob unconditionally + leaves display_name untouched when
// blank). The visible signal is the computed age, not the date —
// what shows on the public profile too.
function AgeStep() {
  const initialDob = sessionStore.getUser()?.dob ?? '';
  const [dob, setDob] = useState(initialDob);
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [err, setErr] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 18 years ago — the highest DOB that still passes the 18+ check.
  // Also caps the native picker, so users can't tab to a future date.
  const maxValidDob = new Date();
  maxValidDob.setFullYear(maxValidDob.getFullYear() - 18);
  const maxValidDobISO = maxValidDob.toISOString().slice(0, 10);

  const age = useMemo(() => {
    if (!dob) return null;
    const [y, m, d] = dob.split('-').map(Number);
    if (!y || !m || !d) return null;
    const now = new Date();
    let a = now.getFullYear() - y;
    if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) a -= 1;
    return a;
  }, [dob]);

  const handleChange = (value: string) => {
    setDob(value);
    setErr(null);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    // Don't fire a save until the value parses and passes 18+ client-side.
    if (!value || value < '1900-01-01') return;
    // Debounce: native date inputs fire onChange as the user types, so
    // wait until they're done before hitting the server.
    saveTimer.current = setTimeout(async () => {
      setSaving('saving');
      try {
        const updated = await api.completeProfile({ dob: value });
        const access = sessionStore.getAccess();
        const refresh = sessionStore.getRefresh();
        if (access && refresh) sessionStore.set(access, refresh, updated);
        setSaving('saved');
        // Fade the "Saved" label after a moment.
        setTimeout(() => setSaving((s) => (s === 'saved' ? 'idle' : s)), 1500);
      } catch (e) {
        setSaving('error');
        setErr(friendlyError(e, 'Builder'));
      }
    }, 600);
  };

  return (
    <StepShell
      eyebrow="03 · Age"
      title="How old are you?"
      sub="Only your age is shown on your profile — never the exact date."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Field
          label={
            <>
              Date of birth{' '}
              <span style={{ color: 'var(--coral)', fontWeight: 600 }}>
                · you must be 18+
              </span>
            </>
          }
        >
          <input
            type="date"
            className="builder-dob-input"
            value={dob}
            onChange={(e) => handleChange(e.target.value)}
            max={maxValidDobISO}
            min="1900-01-01"
            style={{
              ...readOnlyField,
              fontFamily: 'var(--font-ui)',
              fontSize: 16,
              color: dob ? 'var(--ink)' : 'var(--ink-3)',
              cursor: 'text',
              colorScheme: 'light',
              border: '1.5px solid var(--line)',
            }}
            // The native picker accepts the future via spinner controls
            // on some browsers; the max attribute is hint-only there.
          />
        </Field>

        {/* Live age preview — only renders once a date is picked. Before
            that the date input above is enough; the eyebrow + placeholder
            felt redundant. Save status piggybacks off the same block since
            you can't have save state without a value. */}
        {age != null && (
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
            <div
              className="serif"
              style={{ fontSize: 28, fontStyle: 'italic', fontWeight: 500, letterSpacing: -0.4, lineHeight: 1.05 }}
            >
              You'll show as <span style={{ color: 'var(--coral)' }}>{age}</span>
            </div>
            {saving === 'saving' && (
              <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>
                Saving…
              </span>
            )}
            {saving === 'saved' && (
              <span className="mono" style={{ fontSize: 11, color: 'var(--coral)', letterSpacing: 1, textTransform: 'uppercase' }}>
                Saved
              </span>
            )}
          </div>
        )}

        {age != null && age < 18 && (
          <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', borderRadius: 12, padding: '10px 14px', fontSize: 13 }}>
            You need to be 18 or older to use Would You Date Me.
          </div>
        )}

        {err && (
          <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', borderRadius: 12, padding: '10px 14px', fontSize: 13 }}>
            {err}
          </div>
        )}
      </div>
    </StepShell>
  );
}

function BioStep({ bio, onChange }: { bio: string; onChange: (b: string) => void }) {
  const pct = Math.min(100, (bio.length / 500) * 100);
  const nearLimit = bio.length > 450;
  return (
    <StepShell
      eyebrow="04 · Bio"
      title="The sell."
      sub="500 characters. Be specific — abstract adjectives (kind! funny! adventurous!) scan as invisible."
    >
      <textarea
        value={bio}
        onChange={(e) => onChange(e.target.value.slice(0, 500))}
        placeholder="Mostly making ceramics. Occasionally making dinner. Looking for someone who'll argue with me about movies and then still share popcorn."
        className="builder-prose-cap"
        style={{
          width: '100%',
          minHeight: 180,
          resize: 'none',
          background: 'var(--surface)',
          border: '1.5px solid transparent',
          borderRadius: 14,
          padding: 16,
          fontFamily: 'var(--font-ui)',
          fontSize: 15,
          color: 'var(--ink)',
          lineHeight: 1.55,
          outline: 'none',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 10, gap: 8 }}>
        <div style={{ width: 60, height: 3, background: 'var(--line-strong)', borderRadius: 3, overflow: 'hidden' }}>
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: nearLimit ? 'var(--coral)' : 'var(--ink)',
              transition: 'width 0.2s',
            }}
          />
        </div>
        <div
          className="mono"
          style={{ fontSize: 11, color: nearLimit ? 'var(--coral)' : 'var(--ink-3)' }}
        >
          {bio.length}/500
        </div>
      </div>

      {/* "Try this angle" suggestion chips removed — the placeholder
          + the prose sub already prompt the user toward a concrete
          angle; the extra chip row crowded the screen without
          adding signal. */}
    </StepShell>
  );
}

// Map machine-readable reason codes the backend sends in
// ?spotify=error&reason=<code> to user-friendly copy.
function humanReasonForSpotifyError(reason: string): string {
  switch (reason) {
    case 'access_denied':
      return 'You declined the Spotify permission. Try again if you change your mind.';
    case 'state_invalid':
      return 'Your connect request expired — start again from this page.';
    case 'exchange_failed':
      return "Spotify rejected the connect attempt. Check the api logs for details, then try again.";
    case 'profile_failed':
      return "Couldn't read your Spotify profile. Try again in a moment.";
    case 'not_test_user':
      return "Your Spotify account isn't on the test-user list for this app. Add yourself in the Spotify Developer dashboard → User Management → Add New User, then try again.";
    case 'save_failed':
      return "Couldn't save the Spotify connection. Check the api logs for details.";
    case '':
      return 'Spotify connect failed.';
    default:
      return `Spotify connect failed: ${reason}`;
  }
}

function MusicStep() {
  const [conn, setConn] = useState<SpotifyConnection | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  // OAuth callback redirects to /builder?spotify=connected or
  // /builder?spotify=error&reason=<machine_reason>. Surface the human
  // version of the reason, then strip the params so refresh doesn't
  // keep showing the toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('spotify');
    if (status === 'error') {
      const reason = params.get('reason') || '';
      setActionErr(humanReasonForSpotifyError(reason));
    }
    if (status) {
      const url = new URL(window.location.href);
      url.searchParams.delete('spotify');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const c = await spotifyApi.me();
      setConn(c);
      setLoadErr(null);
    } catch (e) {
      if (e instanceof APIError && e.code === 'SPOTIFY_NOT_CONFIGURED') {
        setLoadErr('not_configured');
      } else {
        setLoadErr(friendlyError(e, 'Builder'));
      }
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    setActionErr(null);
    try {
      const { authorize_url } = await spotifyApi.connect();
      // Tell BuilderPage to land us back on step 3 when the OAuth
      // callback redirects to /builder. Survives the cross-origin
      // round-trip via sessionStorage (per-tab, same-origin persistent).
      sessionStorage.setItem(BUILDER_STEP_HANDOFF_KEY, String(MUSIC_STEP));
      window.location.href = authorize_url; // full nav — OAuth round-trip
    } catch (e) {
      setActionErr(friendlyError(e, 'Builder'));
      setBusy(false);
    }
  };

  // Refresh = hit /me with ?force=true so the backend skips the 24h
  // cache and re-pulls from Spotify. Without the force flag the
  // handler would short-circuit on the fresh cache and return the
  // same data, defeating the button.
  const refresh = async () => {
    setBusy(true);
    setActionErr(null);
    try {
      const c = await spotifyApi.me({ force: true });
      setConn(c);
    } catch (e) {
      setActionErr(friendlyError(e, 'Builder'));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm('Disconnect Spotify? Your top tracks will disappear from your profile.')) return;
    setBusy(true);
    setActionErr(null);
    try {
      await spotifyApi.disconnect();
      setConn({ connected: false, tracks: [], artists: [] });
    } catch (e) {
      setActionErr(friendlyError(e, 'Builder'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <StepShell
      eyebrow="05 · Music"
      title="Optional, but telling."
      sub="Your top 3 tracks from the last 4 weeks appear on your profile. Nothing else is shared."
    >
      {loadErr === 'not_configured' ? (
        <PlaceholderCard
          title="Spotify integration isn't configured"
          body="The Spotify Developer credentials aren't set on this environment. Add SPOTIFY_CLIENT_ID + SPOTIFY_CLIENT_SECRET to enable."
        />
      ) : loadErr ? (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13 }}>
          {loadErr}
        </div>
      ) : !conn ? (
        <p style={{ color: 'var(--ink-3)' }}>Loading…</p>
      ) : conn.connected ? (
        <>
          {/* Connected text on row 1, Refresh/Disconnect on row 2.
              Vertical stack keeps the buttons readable on narrow
              widths + gives an even rhythm to the "Top tracks" label
              below (was: row layout with buttons on the right edge,
              which on mobile pushed them past the screen). */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 24, gap: 12 }}>
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>
              Connected{conn.display_name ? ` · ${conn.display_name}` : ''}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={refresh}
                disabled={busy}
                title="Refetch top tracks + artists from Spotify"
                style={{
                  background: 'transparent', border: '1px solid var(--line)',
                  color: 'var(--ink-2)', borderRadius: 8, padding: '6px 12px',
                  fontSize: 12, cursor: busy ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-ui)',
                }}
              >
                {busy ? '…' : 'Refresh'}
              </button>
              <button
                type="button"
                onClick={disconnect}
                disabled={busy}
                style={{
                  background: 'transparent', border: '1px solid var(--line)',
                  color: 'var(--ink-2)', borderRadius: 8, padding: '6px 12px',
                  fontSize: 12, cursor: busy ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-ui)',
                }}
              >
                Disconnect
              </button>
            </div>
          </div>

          {/* Tracks + artists side-by-side (stacks below 480px). Cards
              use the shared MusicCards components so the layout is
              identical to what visitors see on /p/:slug. */}
          <MusicSplit
            tracks={conn.tracks}
            artists={conn.artists}
            tracksLabel="Top tracks · 4 wks"
            artistsLabel="Top artists"
            tracksEmpty="No top tracks yet — Spotify needs a few days of listening data."
            artistsEmpty="No top artists yet."
          />
        </>
      ) : (
        <>
          <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.55, marginTop: 0, marginBottom: 16 }}>
            We pull your top 3 tracks and top 3 artists from the last 4 weeks. Refreshed daily. We never see your listening history, who you follow, or anything else.
          </p>
          {/* Wrapper centers the button on desktop; on mobile the
              button itself goes full-width via the .builder-spotify-
              connect base rule. */}
          <div className="builder-spotify-connect-wrap">
            <button
              type="button"
              onClick={connect}
              disabled={busy}
              className="builder-spotify-connect"
              style={{
                background: '#1ED760', color: '#000',
                padding: '14px 24px', borderRadius: 100,
                fontWeight: 700, fontSize: 15,
                border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-ui)',
                boxShadow: 'var(--shadow-card)',
                gap: 10,
              }}
            >
              <SpotifyLogo />
              {busy ? 'Connecting…' : 'Connect Spotify'}
            </button>
          </div>
        </>
      )}

      {actionErr && (
        <div style={{ marginTop: 14, background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13 }}>
          {actionErr}
        </div>
      )}
    </StepShell>
  );
}

// Official Spotify glyph. Inline SVG so we don't depend on an external
// asset + the colour follows currentColor (we set the button's color to
// #000 to satisfy Spotify's brand-guideline "use black on green" rule).
function SpotifyLogo({ size = 20 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
    </svg>
  );
}

// (Old TrackRow / ArtistRow / SubsectionLabel / EmptyMusicNote moved to
// the shared MusicSplit + MusicCards.css so Builder and PublicProfile
// look identical.)

// ── small primitives ───────────────────────────────────────────────────────

function StepShell({
  eyebrow,
  title,
  sub,
  children,
}: {
  eyebrow: string;
  // title + sub are both optional — some steps (Basics) only need the
  // eyebrow plus the labelled fields themselves.
  title?: string;
  // string for the common single-paragraph case, string[] for multi-
  // paragraph (e.g. Photos step now passes two paragraphs: the lead-
  // photo instruction + the tips that used to live in a separate card).
  sub?: string | string[];
  children: React.ReactNode;
}) {
  const subParas = Array.isArray(sub) ? sub : sub ? [sub] : [];
  return (
    <>
      {/* Eyebrow hidden on mobile — the progress bar at the top of
          the builder already shows "where am I?", and "01 · Photos"
          duplicates the same signal in the small viewport. Desktop
          keeps the eyebrow for visual hierarchy + scanability. */}
      <div
        className="mono hide-on-mobile"
        style={{ fontSize: 11, color: 'var(--coral)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: title ? 10 : 24 }}
      >
        {eyebrow}
      </div>
      {title && (
        <h1
          className="serif"
          style={{ fontSize: 32, fontStyle: 'italic', fontWeight: 500, letterSpacing: -0.5, lineHeight: 1.05, marginBottom: subParas.length > 0 ? 10 : 24 }}
        >
          {title}
        </h1>
      )}
      {subParas.map((p, i) => (
        <p
          key={i}
          className="builder-prose-cap"
          style={{
            fontSize: 14,
            color: 'var(--ink-3)',
            lineHeight: 1.5,
            marginTop: i === 0 ? 0 : 6,
            marginBottom: i === subParas.length - 1 ? 24 : 0,
          }}
        >
          {p}
        </p>
      ))}
      {children}
    </>
  );
}

function PlaceholderCard({ title, body }: { title: string; body: string }) {
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        border: '1.5px dashed var(--line-strong)',
        borderRadius: 18,
        padding: '32px 24px',
        textAlign: 'center',
      }}
    >
      <div className="mono" style={{ fontSize: 11, color: 'var(--coral)', letterSpacing: 2, marginBottom: 8 }}>
        COMING SOON
      </div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 20, marginBottom: 8, color: 'var(--ink)' }}>{title}</h3>
      <p style={{ color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.55 }}>{body}</p>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div
        className="mono"
        style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Chip({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 34,
        padding: '0 14px',
        borderRadius: 34,
        background: active ? 'var(--ink)' : 'var(--chip)',
        color: active ? 'var(--bg)' : 'var(--ink)',
        border: 'none',
        fontFamily: 'var(--font-ui)',
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: -0.1,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function ChipRow({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>;
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'rgba(208,64,48,0.08)',
        color: '#a8331f',
        borderRadius: 12,
        padding: '10px 14px',
        fontSize: 13,
        marginTop: 16,
      }}
    >
      {children}
    </div>
  );
}

// ── styles ─────────────────────────────────────────────────────────────────

// Filled-pill nav arrow: dark background + light chevron. Inverted vs
// the original cream-on-cream look so the arrows actually pop against
// the bg. Inline-flex + center alignment so chevrons sit on the
// optical baseline (the raw ‹ / ›  glyphs render with a top-heavy
// metric otherwise).
const iconBtn: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 36,
  background: 'var(--ink)',
  color: 'var(--bg)',
  border: 'none',
  fontSize: 22,
  lineHeight: 1,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
};

const textBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--ink-3)',
  fontSize: 13,
  fontFamily: 'var(--font-ui)',
  cursor: 'pointer',
};

const readOnlyField: React.CSSProperties = {
  width: '100%',
  background: 'var(--surface)',
  borderRadius: 14,
  padding: '14px 16px',
  fontFamily: 'var(--font-ui)',
  fontSize: 15,
  color: 'var(--ink-2)',
  border: '1.5px solid transparent',
};

// Width is controlled by .builder-footer-cta in Builder.css so the
// button shrinks to natural width on desktop. The class always applies
// to footer CTAs; mobile gets full width via the base class rule.
const btnPrimary = (enabled: boolean): React.CSSProperties => ({
  background: 'var(--coral)',
  color: '#fff',
  padding: '16px 24px',
  borderRadius: 100,
  fontWeight: 600,
  fontSize: 15,
  boxShadow: enabled ? 'var(--shadow-card)' : 'none',
  border: 'none',
  opacity: enabled ? 1 : 0.4,
  cursor: enabled ? 'pointer' : 'not-allowed',
});

// ── photo-slot styles ─────────────────────────────────────────────────────

const scanBanner: React.CSSProperties = {
  background: 'var(--coral-soft)',
  color: 'var(--coral-ink)',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 13,
  lineHeight: 1.4,
};

const rejectedBanner: React.CSSProperties = {
  marginTop: 8,
  background: 'rgba(208,64,48,0.08)',
  color: '#a8331f',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 13,
  lineHeight: 1.4,
};

const photoSlotBase = (filled: boolean): React.CSSProperties => ({
  aspectRatio: '3/4',
  position: 'relative',
  borderRadius: 14,
  overflow: 'hidden',
  background: filled ? 'transparent' : 'var(--surface-2)',
  border: filled ? 'none' : '1.5px dashed var(--line-strong)',
});

const emptySlotBtn: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
};

const leadBadge: React.CSSProperties = {
  position: 'absolute', top: 6, left: 6,
  background: 'var(--coral)', color: '#fff',
  fontSize: 9, padding: '2px 6px', borderRadius: 4,
  fontWeight: 700, letterSpacing: 1,
};

const uploadingOverlay: React.CSSProperties = {
  position: 'absolute', inset: 0,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const rejectedOverlay: React.CSSProperties = {
  position: 'absolute', inset: 0,
  background: 'rgba(208,64,48,0.85)',
  color: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const removeBtn: React.CSSProperties = {
  position: 'absolute', top: 6, right: 6,
  width: 24, height: 24, borderRadius: 24,
  background: 'rgba(0,0,0,0.6)', color: '#fff',
  border: 'none', fontSize: 16, lineHeight: 1,
  cursor: 'pointer', padding: 0,
};
